import React from 'react';
import i18next from 'i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import get from 'lodash.get';

import Link from 'components/Link';
import TypeIcon from 'components/TypeIcon';
import TeasePicture from 'components/TeasePicture';
import Unibrow from 'components/Unibrow';

import AIMS_FLAVORS from 'lib/aimsFlavors';
import { timeFrom } from 'lib/DateTime';
import { imageAltForItem } from 'lib/imageAlt';
import { package as PackagePropType } from 'lib/CustomPropTypes';
import { getSiteConfigs } from 'lib/vertical';
import { getUniformTeaseItem, isThinkModule } from 'lib/teaseUtils';


const POSSIBLE_METADATA = {
  TEASE: 'showTeaseImage',
  TIMESTAMP: 'showTimestamp',
  DEK: 'showDek',
  EYEBROW: 'showEyebrow',
  BRAND: 'showBrand',
};

const renderArticleImage = ({
  showImage, tease, additionalClasses, content, url,
}) => {
  if (!showImage || !tease) {
    return null;
  }

  const {
    type,
  } = content;

  return (
    <Link
      className={
          classNames(
            'wide-tease-item__image-wrapper',
            'flex-none',
            'relative',
            additionalClasses,
          )
        }
      to={url}
      data-test="wide-tease-image"
    >

      <TeasePicture
        className="wide-tease-item__image"
        type={type}
        placeholder={false}
        retina
        responsiveFlavors={{
          s: AIMS_FLAVORS.FOCAL_200x100,
          m: AIMS_FLAVORS.FOCAL_220x110,
          l: AIMS_FLAVORS.FOCAL_280x140,
          xl: AIMS_FLAVORS.FOCAL_260x130,
        }}
        url={tease}
        alt={imageAltForItem(content)}
      />
      <TypeIcon
        type={type}
        className="wide-tease-item__type-icon"
      />
    </Link>
  );
};

const getBrand = ({ vertical, showBrand, additionalClasses }) => {
  if (!showBrand || !vertical) {
    return null;
  }


  const brandUnibrand = {
    text: vertical,
  };

  const config = getSiteConfigs(vertical.toLowerCase());
  if (config && config.siteUrl) {
    brandUnibrand.url = {
      primary: config.siteUrl,
    };
  }

  return (
    <Unibrow
      className={classNames(additionalClasses, 'wide-tease-item__unibrow')}
      unibrow={brandUnibrand}
    />
  );
};

const getUnibrow = ({
  additionalClasses,
  content,
  showEyebrow,
  unibrow,
}) => {
  if (!showEyebrow || !unibrow) {
    return null;
  }

  const moduleClass = isThinkModule(content);

  return (
    <Unibrow
      className={classNames(moduleClass, additionalClasses, 'wide-tease-item__unibrow')}
      unibrow={unibrow}
    />
  );
};

const renderUnibrow = ({
  additionalClasses,
  content,
  dataAttributes,
  showBrand,
  showEyebrow,
  unibrow,
  vertical,
}) => {
  const unibrowComp = getUnibrow({
    additionalClasses,
    content,
    showEyebrow,
    unibrow,
  });
  const brandComp = getBrand({ vertical, showBrand, additionalClasses });

  if (!unibrowComp && !brandComp) {
    return null;
  }

  const separator = unibrowComp && brandComp
    ? (<div className={classNames('wide-tease-item__unibrow-separator', additionalClasses)}> / </div>)
    : '';

  return (
    <div className="wide-tease-item__unibrow-wrapper df" data-test={dataAttributes}>
      {brandComp}
      {separator}
      {unibrowComp}
    </div>
  );
};

const renderUnibrowAndDate = ({
  content,
  datePublished,
  showBrand,
  showEyebrow,
  showTimestamp,
  unibrow,
  vertical,
}) => {
  const hasDate = showTimestamp && !!datePublished;
  const unibrowComps = renderUnibrow({
    additionalClasses: 'dib dn-m',
    content,
    showBrand,
    showEyebrow,
    unibrow,
    vertical,
  });
  const hasUnibrow = !!unibrowComps;

  const showEyebrowWrapper = hasUnibrow || hasDate;
  if (!showEyebrowWrapper) {
    return null;
  }

  return (
    <div
      className={
          classNames(
            'wide-tease-item__unibrow-timestamp',
            'df',
            'flex-none-m',
            {
              'dn-m': !hasDate && hasUnibrow,
            },
          )
        }
    >
      {unibrowComps}
      {hasDate && (
        <div
          className={classNames('wide-tease-item__timestamp dib db-m',
            {
              ml3: hasUnibrow,
              'ml0-m': hasUnibrow,
            })}
          data-test="wide-tease-date"
        >
          {`${i18next.t('time_ago', { time: timeFrom(datePublished) })}`}
        </div>
      )}
    </div>
  );
};

const getMetadata = (path, props) => {
  const {
    content: {
      metadata,
    },
  } = props;
  return get(metadata, path, true);
};

function WideTease(props) {
  const {
    isRail,
    isLastArticle,
    hideBorder,
    content,
  } = props;

  const unifiedItem = getUniformTeaseItem(content);

  const {
    datePublished,
    description,
    headline,
    tease,
    unibrow,
    vertical,
    url,
  } = unifiedItem;

  const showDek = getMetadata(POSSIBLE_METADATA.DEK, props);
  const showTimestamp = getMetadata(POSSIBLE_METADATA.TIMESTAMP, props);
  const showBrand = getMetadata(POSSIBLE_METADATA.BRAND, props);
  const showEyebrow = getMetadata(POSSIBLE_METADATA.EYEBROW, props);
  const showImage = getMetadata(POSSIBLE_METADATA.TEASE, props);
  const hasImage = showImage && !!tease;


  return (
    <div
      className={classNames(
        'wide-tease-item__wrapper',
        'df',
        'flex-column',
        'flex-row-m',
        'flex-nowrap-m',
        {
          'wide-tease-item__wrapper--last-page': isLastArticle,
          'wide-tease-item__wrapper--no-border': hideBorder,
        },
      )}
      data-contentid={content.id}
      key={content.id}
    >
      {renderUnibrowAndDate({
        content,
        datePublished,
        showBrand,
        showEyebrow,
        showTimestamp,
        unibrow,
        vertical,
      })}
      <div
        className={classNames(
          'wide-tease-item__info-wrapper',
          'flex-grow-1-m',
          {
            'wide-tease-item__info-wrapper--no-art': !hasImage && !isRail,
          },
        )}
        data-test="wide-tease-info-wrapper"
      >
        {renderUnibrow({
          additionalClasses: 'dn db-m',
          content,
          dataAttributes: 'wide-tease-unibrow',
          showBrand,
          showEyebrow,
          unibrow,
          vertical,
        })}
        <Link
          to={url}
        >
          <h2
            className="wide-tease-item__headline"
            data-test="wide-tease-headline"
          >
            {headline}
          </h2>
        </Link>
        <div className={classNames({ df: hasImage })}>
          {showDek && (
            <div className="wide-tease-item__description" data-test="wide-tease-dek">{description}</div>
          )}
          {renderArticleImage({
            tease, showImage, additionalClasses: 'dt dn-m', content, url,
          })}
        </div>
      </div>

      {renderArticleImage({
        tease, showImage, additionalClasses: 'dn dt-m', content, url,
      })}
    </div>
  );
}

WideTease.propTypes = {
  content: PackagePropType.isRequired,
  hideBorder: PropTypes.bool,
  isLastArticle: PropTypes.bool,
  isRail: PropTypes.bool,
};

WideTease.defaultProps = {
  hideBorder: false,
  isLastArticle: false,
  isRail: false,
};

export default WideTease;
